const ONE_LINE_MAX_LENGTH = 25
const TWO_LINES_MAX_LENGTH = 80

const spacePositions = (text: string): number[] => {
	const positions: number[] = []
	for (let i = 0; i < text.length; i++) {
		if (text[i] === ' ') positions.push(i)
	}
	return positions
}

const splitAtSpaces = (text: string, lineCount: number): string[] => {
	const spaces = spacePositions(text)
	const cuts: number[] = []
	for (let line = 1; line < lineCount; line++) {
		const target = (text.length * line) / lineCount
		const previousCut = cuts.length ? cuts[cuts.length - 1] : -1
		const candidates = spaces.filter(space => space > previousCut)
		if (!candidates.length) break
		cuts.push(
			candidates.reduce((best, space) =>
				Math.abs(space - target) < Math.abs(best - target) ? space : best
			)
		)
	}

	const lines: string[] = []
	let start = 0
	cuts.forEach(cut => {
		lines.push(text.slice(start, cut))
		start = cut + 1
	})
	lines.push(text.slice(start))
	return lines.map(line => line.trim()).filter(line => line.length > 0)
}

/**
 * Chart label for a poll answer. Short answers stay on one line; longer ones
 * are split into two or three lines of similar length, always at a space so a
 * word is never broken in half.
 */
export const splitChartLabel = (text: string): string | string[] => {
	if (text.length < ONE_LINE_MAX_LENGTH) return text
	const lineCount = text.length < TWO_LINES_MAX_LENGTH ? 2 : 3
	return splitAtSpaces(text, lineCount)
}

// A one-line answer label is drawn at 16 px: about 9 px a character, plus
// room either side so neighbouring labels never touch.
const LABEL_CHAR_WIDTH = 9
const LABEL_SPACING = 24
const MIN_ANSWER_WIDTH = 64

/**
 * The room each standing bar needs for the longest one-line answer label to
 * fit under it. Short answers ('Yes', 'No') need little; when the answers
 * together need more than the box has, the bars lie down instead.
 */
export const answerSlotWidth = (labels: string[]): number =>
	Math.max(
		MIN_ANSWER_WIDTH,
		...labels.map(label => label.length * LABEL_CHAR_WIDTH + LABEL_SPACING)
	)

/** Word by word, lines of at most `maxChars`; a longer word stays whole. */
const wrapAtSpaces = (text: string, maxChars: number): string[] =>
	text
		.split(' ')
		.filter(word => word.length > 0)
		.reduce<string[]>((lines, word) => {
			const last = lines[lines.length - 1]
			if (last !== undefined && last.length + 1 + word.length <= maxChars) {
				lines[lines.length - 1] = `${last} ${word}`
			} else {
				lines.push(word)
			}
			return lines
		}, [])

/**
 * The label for a column `width` px wide: kept as it is when every line fits,
 * otherwise wrapped again word by word to the column.
 */
export const fitChartLabel = (
	label: string | string[],
	width: number
): string | string[] => {
	const maxChars = Math.max(1, Math.floor(width / LABEL_CHAR_WIDTH))
	const lines = Array.isArray(label) ? label : [label]
	if (lines.every(line => line.length <= maxChars)) return label
	const wrapped = wrapAtSpaces(lines.join(' '), maxChars)
	return wrapped.length === 1 ? wrapped[0] : wrapped
}
